import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

const OffersContext = createContext();

export const useOffers = () => {
  const context = useContext(OffersContext);
  if (!context) {
    throw new Error('useOffers must be used within OffersProvider');
  }
  return context;
};

export const OffersProvider = ({ children }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to offers
  useEffect(() => {
    const q = query(collection(db, 'offers'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOffers(items);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching offers:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Add new offer
  const addOffer = async (offerData) => {
    try {
      const newOffer = {
        ...offerData,
        status: offerData.status || 'active',
        usageCount: 0,
        totalDiscount: 0,
        totalRevenue: 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      const docRef = await addDoc(collection(db, 'offers'), newOffer);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error adding offer:', error);
      return { success: false, error };
    }
  };

  // Update offer
  const updateOffer = async (offerId, updates) => {
    try {
      const offerRef = doc(db, 'offers', offerId);
      await updateDoc(offerRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating offer:', error);
      return { success: false, error };
    }
  };

  // Delete offer
  const deleteOffer = async (offerId) => {
    try {
      await deleteDoc(doc(db, 'offers', offerId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting offer:', error);
      return { success: false, error };
    }
  };

  // Toggle offer status (active/paused)
  const toggleOfferStatus = async (offerId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    return updateOffer(offerId, { status: newStatus });
  };

  // Get active offers
  const getActiveOffers = () => {
    return offers.filter(offer => offer.status === 'active');
  };

  // Get paused offers
  const getPausedOffers = () => {
    return offers.filter(offer => offer.status === 'paused');
  };

  // Get expired offers
  const getExpiredOffers = () => {
    const now = new Date();
    return offers.filter(offer => {
      if (!offer.endDate) return false;
      const endDate = offer.endDate.toDate ? offer.endDate.toDate() : new Date(offer.endDate);
      return endDate < now;
    });
  };

  // Get offer stats
  const getOfferStats = () => {
    const activeOffers = getActiveOffers();
    const totalUsage = offers.reduce((sum, o) => sum + (o.usageCount || 0), 0);
    const totalDiscount = offers.reduce((sum, o) => sum + (o.totalDiscount || 0), 0);
    const totalRevenue = offers.reduce((sum, o) => sum + (o.totalRevenue || 0), 0);

    return {
      totalOffers: offers.length,
      activeOffers: activeOffers.length,
      pausedOffers: getPausedOffers().length,
      expiredOffers: getExpiredOffers().length,
      totalUsage,
      totalDiscount,
      totalRevenue
    };
  };

  // Validate promo code
  const validatePromoCode = (code) => {
    const offer = offers.find(o =>
      o.promoCode?.toLowerCase() === code.toLowerCase() &&
      o.status === 'active'
    );

    if (!offer) {
      return { valid: false, message: 'Invalid promo code' };
    }

    // Check expiry
    if (offer.endDate) {
      const endDate = offer.endDate.toDate ? offer.endDate.toDate() : new Date(offer.endDate);
      if (endDate < new Date()) {
        return { valid: false, message: 'Promo code has expired' };
      }
    }

    return { valid: true, offer };
  };

  // Recommended offers templates
  const recommendedOffers = [
    {
      title: '50% OFF up to ₹100',
      type: 'percentage',
      discountValue: 50,
      maxDiscount: 100,
      minOrderValue: 199,
      description: 'Great for attracting new customers'
    },
    {
      title: '40% OFF up to ₹80',
      type: 'percentage',
      discountValue: 40,
      maxDiscount: 80,
      minOrderValue: 149,
      description: 'Popular choice for regular offers'
    },
    {
      title: '30% OFF up to ₹75',
      type: 'percentage',
      discountValue: 30,
      maxDiscount: 75,
      minOrderValue: 99,
      description: 'Good for frequent customers'
    },
    {
      title: 'Flat ₹50 OFF',
      type: 'flat',
      discountValue: 50,
      minOrderValue: 199,
      description: 'Simple and effective'
    },
    {
      title: 'Free Delivery',
      type: 'free_delivery',
      minOrderValue: 99,
      description: 'Remove delivery charges'
    }
  ];

  const value = {
    offers,
    loading,
    addOffer,
    updateOffer,
    deleteOffer,
    toggleOfferStatus,
    getActiveOffers,
    getPausedOffers,
    getExpiredOffers,
    getOfferStats,
    validatePromoCode,
    recommendedOffers
  };

  return (
    <OffersContext.Provider value={value}>
      {children}
    </OffersContext.Provider>
  );
};
